import React from 'react'

/**
 * 懒加载 chunk 兜底重试工具。
 *
 * 背景:发版后,仍运行旧 `main.js` 的标签页在进入未加载过的懒加载路由时,会请求
 * 旧构建的 `*.chunk.js`。该 chunk 在新快照里已不存在,被 SPA 回退规则返回
 * `index.html`(HTTP 200,body 为 HTML),浏览器把 HTML 当 JS 解析失败,抛出
 * `ChunkLoadError` 导致白屏。
 *
 * 策略(第一层兜底):捕获 chunk 加载失败 → 用 sessionStorage 时间戳做防循环保护
 * (冷却窗口内不重复刷新)→ `window.location.reload()` 一次拉取最新 `index.html`
 * 与 chunk 映射;刷新阻塞期间返回一个永不 resolve 的 promise,避免在跳转前闪现
 * 错误屏。当 chunk 确实无法加载(如离线)时,冷却保护生效,不会无限刷新,错误会
 * 正常向上抛出交给 ErrorBoundary。
 */

const RELOAD_STORAGE_KEY = 'chunk-reload-ts'

/** 冷却窗口:窗口内不重复刷新,避免离线等持续失败场景下无限刷新 */
const RELOAD_COOLDOWN_MS = 10_000

/** 判断是否为 chunk / 动态 import 加载失败 */
function isChunkLoadError(error: unknown): boolean {
  if (!error) return false
  const name = (error as { name?: string }).name ?? ''
  const message = (error as { message?: string }).message ?? ''
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk [\w-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  )
}

/** 最近是否已经因 chunk 失败刷新过(冷却窗口内) */
function hasReloadedRecently(): boolean {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_STORAGE_KEY)
    if (!raw) return false
    const last = Number(raw)
    if (Number.isNaN(last)) return false
    return Date.now() - last < RELOAD_COOLDOWN_MS
  } catch {
    // sessionStorage 不可用(隐私模式等)→ 视为未刷新过,允许刷新一次
    return false
  }
}

/** 记录本次刷新时间戳 */
function markReloaded(): void {
  try {
    window.sessionStorage.setItem(RELOAD_STORAGE_KEY, String(Date.now()))
  } catch {
    // 忽略存储失败
  }
}

/**
 * 包装动态 import:加载失败且判定为 chunk 失效时,刷新一次以恢复。
 */
async function loadWithRetry<T>(factory: () => Promise<T>): Promise<T> {
  try {
    return await factory()
  } catch (error) {
    if (isChunkLoadError(error) && !hasReloadedRecently()) {
      markReloaded()
      window.location.reload()
      // 刷新已触发,返回永不 resolve 的 promise,阻塞渲染直到页面重载,避免闪现错误屏
      return new Promise<T>(() => {})
    }
    // 已在冷却窗口内刷新过,或非 chunk 错误 → 交给上层 ErrorBoundary
    throw error
  }
}

/**
 * `React.lazy` 的替代品,自动为懒加载组件加上 chunk 失败兜底重试。
 * 用法与 `React.lazy` 完全一致:
 *
 * ```ts
 * const Page = lazyWithRetry(() => import('@/pages/Foo'))
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return React.lazy(() => loadWithRetry(factory))
}
